import { tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import dateUtils from 'donor-elf-web/lib/date-utils';
import JournalDecisionInfo from 'donor-elf-web/lib/models/journal-decision-info';
import ContactModel from 'donor-elf-web/models/contact';
import JournalContactModel from 'donor-elf-web/models/journal-contact';
import JournalContactDetailModel from 'donor-elf-web/models/all-funds/journal-contact-detail';
import JournalDecisionModel from 'donor-elf-web/models/journal-decision';
import PledgeModel from 'donor-elf-web/models/pledge';
import RecommendationModel from 'donor-elf-web/models/recommendation';
import V2TaskModel from 'donor-elf-web/models/v2-task';
import JournalInfo from './journal-info';

const SORT_TASKS_BY = ['isComplete:asc', 'completedDate:desc', 'dueDate:asc'];

export type DecisionStageNames =
  | 'notStarted'
  | 'preAsk'
  | 'waitingOnDecision'
  | 'decisionMade';

export default class JournalContactInfo {
  static _TODAY: Date;
  static _TOMORROW: Date;

  static get TODAY(): Date {
    if (this._TODAY) {
      return this._TODAY;
    }
    return (this._TODAY = dateUtils.serializeToDateOnly(
      dateUtils.correctTimezone(new Date()),
    ));
  }

  static get TOMORROW(): Date {
    if (this._TOMORROW) {
      return this._TOMORROW;
    }
    let tomorrow = dateUtils.correctTimezone(new Date());
    tomorrow.setDate(tomorrow.getDate() + 1);
    return (this._TOMORROW = dateUtils.serializeToDateOnly(tomorrow));
  }

  constructor(args: {
    journalContactDetailModel: JournalContactDetailModel;
    journalInfo: JournalInfo;
    contact?: ContactModel;
  }) {
    this.journalInfo = args.journalInfo;
    this.contact = args.contact;
    this.contactId = args.contact?.id;
    this.updateFromJournalContactDetailModel(args.journalContactDetailModel);
    this.journalId = this.journalContact.journalId;
  }

  contact?: ContactModel;
  contactId?: string;
  journalInfo: JournalInfo;
  journalId: string;
  @tracked declare journalContact: JournalContactModel;
  @tracked declare journalDecisions: JournalDecisionModel[];
  @tracked declare journalDecisionInfos: JournalDecisionInfo[];
  @tracked declare pledges: PledgeModel[];
  tasks!: V2TaskModel[];
  recommendations!: RecommendationModel[];
  @tracked lastGift?: any;
  @tracked existingPledge?: PledgeModel;

  // properties below are set in the recalculate() function
  @tracked tasksByStageId: { [stageId: string]: V2TaskModel[] } = {};
  @tracked lastTaskUpdatedAt?: Date;
  @tracked currentStageIndex: number = -1;
  @tracked lastCompletedStageIndex: number = -1;
  @tracked lastStageIsCompleted: boolean = false;
  @tracked hasNotCompleteTasks: boolean = false;
  @tracked hasNotCompleteDueByTodayTask: boolean = false;
  @tracked earliestNotCompleteDueDate?: Date;
  // WARNING: this can contain contacts ids that are not part of the current journal
  @tracked recommendedContactIds?: string[];
  // WARNING: this can contain contacts ids that are not part of the current journal
  @tracked recommendedByContactIds?: string[];
  // { (notStarted|preAsk|WaitingOnDecision|decisionMade): true }
  @tracked decisionStage: { [decisionStage in DecisionStageNames]?: boolean } =
    {};

  @tracked declare decisionMade: boolean;

  recalculate() {
    this.tasksByStageId = {};
    this.hasNotCompleteTasks = false;
    this.hasNotCompleteDueByTodayTask = false;
    let stageIdIndex: any = {};
    this.journalInfo.sortedStages.forEach((stage, index) => {
      this.tasksByStageId[stage.id] = [];
      stageIdIndex[stage.id] = index;
    });

    let decisionStage:
      | { [decisionStage in DecisionStageNames]?: boolean }
      | undefined;
    let lastTaskUpdatedAt: Date | undefined;
    let currentStageIndex = -1;
    let lastCompletedStageIndex = -1;
    let earliestNotCompleteDueDate: Date | undefined;

    this.decisionMade = this.journalDecisions.length > 0;
    if (this.decisionMade) {
      lastCompletedStageIndex = this.journalInfo.sortedStages.findIndex(
        (x) => x.decisionStage,
      );
      currentStageIndex = lastCompletedStageIndex + 1;
      decisionStage = { decisionMade: true };
    }
    this.tasks.forEach((task) => {
      this.tasksByStageId[task.journalStageId!].push(task);
      if (lastTaskUpdatedAt == null || task.updatedAt! > lastTaskUpdatedAt) {
        lastTaskUpdatedAt = task.updatedAt;
      }
      if (!task.isComplete) {
        this.hasNotCompleteTasks = true;
        if (task.dueDate) {
          let serializedDueDate = dateUtils.serializeToDateOnly(task.dueDate);
          if (
            earliestNotCompleteDueDate == null ||
            serializedDueDate < earliestNotCompleteDueDate
          ) {
            earliestNotCompleteDueDate = serializedDueDate;
          }
          if (serializedDueDate <= JournalContactInfo.TODAY) {
            this.hasNotCompleteDueByTodayTask = true;
          }
        }
      }
      if (!task.isComplete || task.isAttemptedResult) {
        if (currentStageIndex < stageIdIndex[task.journalStageId!]) {
          currentStageIndex = stageIdIndex[task.journalStageId!];
        }
      } else {
        if (lastCompletedStageIndex < stageIdIndex[task.journalStageId!]) {
          lastCompletedStageIndex = stageIdIndex[task.journalStageId!];
        }
        if (currentStageIndex <= lastCompletedStageIndex) {
          currentStageIndex = lastCompletedStageIndex + 1;
        }
      }
    });

    if (decisionStage == null) {
      if (
        this.tasksByStageId[this.journalInfo.askStage.id].find(
          (x) => x.completedDate,
        )
      ) {
        decisionStage = { waitingOnDecision: true };
      } else if (this.tasks.find((x) => x.completedDate)) {
        decisionStage = { preAsk: true };
      } else {
        decisionStage = { notStarted: true };
      }
    }
    this.decisionStage = decisionStage;

    this.journalInfo.sortedStages.forEach((stage) => {
      this.tasksByStageId[stage.id] = sortByProperties(
        this.tasksByStageId[stage.id],
        SORT_TASKS_BY,
      );
    });

    this.lastTaskUpdatedAt = lastTaskUpdatedAt;
    this.currentStageIndex = currentStageIndex;
    this.lastCompletedStageIndex = lastCompletedStageIndex;
    this.lastStageIsCompleted =
      lastCompletedStageIndex === this.journalInfo.sortedStages.length - 1;
    this.earliestNotCompleteDueDate =
      earliestNotCompleteDueDate || JournalContactInfo.TOMORROW;
    this.recommendedContactIds = this.recommendations
      .filter((x) => x.recommendedByContactId === this.contactId)
      .map((x) => x.recommendedContactId);
    this.recommendedByContactIds = this.recommendations
      .filter((x) => x.recommendedContactId === this.contactId)
      .map((x) => x.recommendedByContactId);
  }

  updateFromJournalContactDetailModel(
    journalContactDetailModel: JournalContactDetailModel,
  ) {
    this.journalContact = journalContactDetailModel.journalContact;
    this.journalDecisions = sortByProperties(
      journalContactDetailModel.journalDecisions.slice(),
      ['decisionDate:asc'],
    );
    this.journalDecisionInfos = this.journalDecisions.map(
      (journalDecision) =>
        new JournalDecisionInfo({
          journalDecision,
          journalContactDetailModel,
          journalInfo: this.journalInfo,
        }),
    );
    this.pledges = journalContactDetailModel.pledges.slice();
    this.tasks = journalContactDetailModel.tasks.slice();
    this.recommendations = journalContactDetailModel.recommendations.slice();
    this.lastGift = journalContactDetailModel.lastGift;
    this.existingPledge = journalContactDetailModel.existingPledge;
    this.recalculate();
  }
}
